import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  FileText,
  Loader2,
  Mic2,
  Send,
  Sparkles,
} from "lucide-react";
import Headerdash from "./head";
import Sidebar from "./sidebar";
import Recorder from "../pages/QuestionPage/recorder";
import ResultBox from "../pages/QuestionPage/ResultBox";
import ScoreCard from "../pages/QuestionPage/ScoreCard";
import SpacingBox from "../pages/QuestionPage/Spacing";
import useFetchData from "./fetchpostData";
import ScoreBox from "../pages/QuestionPage/ResponseBox";
import Notification from "../pages/QuestionPage/Notification";
import QuestionBox from "../pages/QuestionPage/questionbox";
import Timers from "./PageComponent/Timers";

const PracticeShell = ({ children }) => (
  <div className="min-h-screen bg-slate-50 text-gray-700">
    <Headerdash />
    <Sidebar />
    <main className="min-h-screen pt-20 sm:pl-72">
      <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {children}
      </div>
    </main>
  </div>
);

const StatusPill = ({ type, children }) => {
  const styles =
    type === "success"
      ? "bg-green-50 text-green-700 ring-green-100"
      : type === "warning"
      ? "bg-amber-50 text-amber-700 ring-amber-100"
      : "bg-purple-50 text-purple-700 ring-purple-100";

  return (
    <span
      className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-bold uppercase ring-1 ${styles}`}
    >
      {children}
    </span>
  );
};

export default function OpenquestionPage(props) {
  const { title } = useParams();
  const section = "readaloud";
  const practiceItemId = title;

  const post = useFetchData(section, title);
  const [originalWords, setOriginalWords] = useState("");
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [isAttempted, setIsAttempted] = useState(false);
  const [message, setMessage] = useState("");
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState("");
  const [savedPractice, setSavedPractice] = useState(null);
  const [result, setResult] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);

  useEffect(() => {
    if (post && post.question) {
      setOriginalWords(post.question);
    }
  }, [post]);

  const savePractice = async (practiceData) => {
    try {
      const response = await fetch("/api/save-readaloud", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(practiceData),
      });
      if (!response.ok) throw new Error("Failed to save practice data");
      return await response.json();
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  };

  useEffect(() => {
    const highlightMatchingWords = () => {
      const words1 = originalWords.split(" ");
      const words2 = text.split(" ");
      setResult(
        words2.map((word2, index) => (
          <span
            key={index}
            style={{
              backgroundColor: words1.includes(word2) ? "#CBFB8A" : "#E74C3C",
              marginRight: "4px",
            }}
          >
            {word2}
          </span>
        ))
      );
    };
    highlightMatchingWords();
  }, [originalWords, text]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!recordedBlob) {
      setMessage("Please record audio first before submitting.");
      setNotificationType("error");
      setShowNotification(true);
      return;
    }

    setLoading(true);

    try {
      const audioForm = new FormData();
      audioForm.append("audio", recordedBlob, "recording.wav");

      const uploadRes = await fetch("/api/upload-audio", {
        method: "POST",
        body: audioForm,
      });

      if (!uploadRes.ok) {
        throw new Error("Audio upload failed");
      }

      const uploadData = await uploadRes.json();

      if (!uploadData.audioUrl) {
        throw new Error("Audio URL missing from upload response");
      }

      const audioUrl = uploadData.audioUrl;
      const transcribeForm = new FormData();
      transcribeForm.append("audio", recordedBlob, "recording.wav");

      const transRes = await fetch("/api/transcribe", {
        method: "POST",
        body: transcribeForm,
      });

      if (!transRes.ok) {
        throw new Error("Transcription request failed");
      }

      const transData = await transRes.json();

      if (!transData.text) {
        throw new Error("Transcription failed to return text");
      }

      const transcribedText = transData.text;
      setText(transcribedText);

      const practiceData = {
        practiceType: section,
        practiceItemId,
        response: {
          questionId: practiceItemId,
          audioUrl: audioUrl,
          audioDuration: uploadData.duration || transData.duration || 0,
          answer: transcribedText,
          transcript: transData.transcript || transcribedText,
          words: transData.words || [],
          transcriptionEngine: transData.engine,
          isAttempted: true,
        },
      };

      const result = await savePractice(practiceData);

      setMessage("Practice data saved successfully!");
      setNotificationType("success");
      setSavedPractice(result);
      setShowNotification(true);

      setTimeout(() => {
        setIsAttempted(true);
        setLoading(false);
        setSubmitted(true);
      }, 1000);
    } catch (error) {
      console.error("Submit error:", error);
      setMessage(error.message || "Something went wrong.");
      setNotificationType("error");
      setShowNotification(true);

      setLoading(false);
      setSubmitted(false);
    }
  };

  return (
    <PracticeShell>
      <section className="mb-6 flex flex-col gap-5 border-b border-slate-200 pb-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase text-purple-700">
            Speaking practice
          </p>
          <h1 className="mt-1 text-2xl font-extrabold text-gray-900 sm:text-3xl">
            {post?.q_title || "Read aloud"}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-gray-500 sm:text-base">
            Read the prompt naturally and clearly, then submit your recording
            for scoring.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <StatusPill>Medium</StatusPill>
          <StatusPill type={isAttempted ? "success" : "warning"}>
            {isAttempted ? "Attempted" : "Not attempted"}
          </StatusPill>
          <span className="inline-flex min-h-9 items-center gap-2 rounded-md bg-white px-3 py-2 text-sm font-bold text-gray-700 shadow-sm ring-1 ring-slate-200">
            <Clock3 className="h-4 w-4 text-purple-700" aria-hidden="true" />
            <Timers initialSeconds={0} />
          </span>
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr),360px]">
        <div className="space-y-6">
          <section className="rounded-md border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="mb-4 flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
                <FileText className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase text-purple-700">
                  Instructions
                </p>
                <h2 className="text-xl font-extrabold text-gray-900">
                  Prepare, record, submit
                </h2>
              </div>
            </div>

            <div className="rounded-md bg-slate-50 p-4 text-sm leading-6 text-gray-600">
              Look at the text below. In the 35 seconds, you must read this
              text aloud as naturally and clearly as possible. You have 35
              seconds to read aloud.
            </div>
          </section>

          <section className="rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
                  <Mic2 className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-sm font-semibold uppercase text-purple-700">
                    Recording
                  </p>
                  <h2 className="text-xl font-extrabold text-gray-900">
                    Your spoken response
                  </h2>
                </div>
              </div>
              {recordedBlob && (
                <span className="inline-flex items-center gap-2 rounded-md bg-green-50 px-2.5 py-1 text-xs font-bold uppercase text-green-700 ring-1 ring-green-100">
                  <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                  Recorded
                </span>
              )}
            </div>

            <Recorder
              text={text}
              setText={setText}
              onRecordingComplete={(blob) => setRecordedBlob(blob)}
              {...props}
              duration={35}
              className="mt-0 mb-0 w-full max-w-xl border border-slate-200 bg-slate-50 shadow-sm"
            />
          </section>

          <section className="rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
            <QuestionBox question={post?.question} />
          </section>

          <section className="flex flex-col gap-3 rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
                <Sparkles className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase text-gray-500">
                  Ready to score?
                </p>
                <p className="text-sm text-gray-500">
                  Submit after recording to upload audio and generate feedback.
                </p>
              </div>
            </div>

            <button
              type="button"
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-violet-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" aria-hidden="true" />
                  Submit
                </>
              )}
            </button>
          </section>

          {submitted && (
            <section className="space-y-5">
              <ResultBox result={result} />
              <ScoreCard
                finalScore={savedPractice?.savedResponse?.totalScore}
                remarks={savedPractice?.savedResponse?.remarks}
                contentScore={savedPractice?.savedResponse?.contentScore}
                fluencyScore={savedPractice?.savedResponse?.fluencyScore}
                pronounctionScore={
                  savedPractice?.savedResponse?.pronounctionScore
                }
                maxScore={savedPractice?.maxScores?.overall || 90}
                traitMaxScore={savedPractice?.maxScores?.content || 90}
              />
              {savedPractice?.readAloudScore?.feedback ? (
                <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
                  <p className="text-sm font-semibold uppercase text-purple-700">
                    AI Feedback
                  </p>
                  <p className="mt-2 text-sm leading-6 text-gray-600">
                    {savedPractice.readAloudScore.feedback.overall}
                  </p>
                  <div className="mt-4 grid gap-3 md:grid-cols-3">
                    <div className="rounded-md bg-blue-50 p-4 ring-1 ring-blue-100">
                      <h3 className="font-bold text-blue-800">Content</h3>
                      <p className="mt-2 text-sm text-blue-900">
                        {savedPractice.readAloudScore.feedback.content}
                      </p>
                    </div>
                    <div className="rounded-md bg-green-50 p-4 ring-1 ring-green-100">
                      <h3 className="font-bold text-green-800">Fluency</h3>
                      <p className="mt-2 text-sm text-green-900">
                        {savedPractice.readAloudScore.feedback.fluency}
                      </p>
                    </div>
                    <div className="rounded-md bg-purple-50 p-4 ring-1 ring-purple-100">
                      <h3 className="font-bold text-purple-800">
                        Pronunciation
                      </h3>
                      <p className="mt-2 text-sm text-purple-900">
                        {savedPractice.readAloudScore.feedback.pronunciation}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 grid gap-3 rounded-md bg-slate-50 p-4 text-sm text-gray-700 ring-1 ring-slate-100 sm:grid-cols-2 lg:grid-cols-4">
                    <span>
                      Accuracy:{" "}
                      <strong>
                        {Math.round(
                          (savedPractice.readAloudScore.content_accuracy || 0) *
                            100
                        )}
                        %
                      </strong>
                    </span>
                    <span>
                      WPM:{" "}
                      <strong>
                        {savedPractice.readAloudScore.speech_rate || 0}
                      </strong>
                    </span>
                    <span>
                      Pauses:{" "}
                      <strong>
                        {savedPractice.readAloudScore.pause_count || 0}
                      </strong>
                    </span>
                    <span>
                      Pronunciation:{" "}
                      <strong>
                        {Math.round(
                          (savedPractice.readAloudScore
                            .pronunciation_accuracy || 0) * 100
                        )}
                        %
                      </strong>
                    </span>
                  </div>
                </div>
              ) : null}
            </section>
          )}
        </div>

        <aside className="space-y-6">
          <section className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-amber-50 text-amber-700">
                <AlertCircle className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase text-gray-500">
                  Practice tip
                </p>
                <p className="mt-1 text-sm leading-6 text-gray-600">
                  Focus on pace, clarity, and natural pauses. Accuracy improves
                  when you read full phrases instead of isolated words.
                </p>
              </div>
            </div>
          </section>

          <ScoreBox
            originalWords={originalWords}
            practiceItemId={practiceItemId}
            onIsAttempted={setIsAttempted}
          />
        </aside>
      </div>

      <SpacingBox />

      {showNotification && (
        <Notification
          message={message}
          type={notificationType}
          onClose={() => setShowNotification(false)}
        />
      )}
    </PracticeShell>
  );
}
